/**
 * Firebase Client Configuration
 * 
 * This module initializes Firebase services for the dashboard.
 * It auto-detects local development and connects to emulators when available.
 * 
 * @module lib/firebase
 */

import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  connectAuthEmulator,
  type Auth 
} from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator,
  type Firestore 
} from 'firebase/firestore';
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions
} from 'firebase/functions';
import { 
  getAnalytics, 
  type Analytics 
} from 'firebase/analytics';

// =============================================================================
// Firebase Configuration
// =============================================================================

/**
 * Firebase configuration loaded from environment variables.
 * These are client-side identifiers and safe to expose.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate required config
const requiredKeys = ['apiKey', 'authDomain', 'projectId', 'appId'] as const;
for (const key of requiredKeys) {
  if (!firebaseConfig[key]) {
    console.error(`Missing required Firebase config: VITE_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`);
  }
}

// =============================================================================
// Emulator Configuration
// =============================================================================

/**
 * Check if we should use Firebase emulators.
 * Set VITE_USE_EMULATORS=true in .env.local for local development.
 */
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

/** Emulator ports (must match firebase.json) */
const EMULATOR_PORTS = {
  auth: 28099,
  firestore: 28080,
  functions: 28001,
  storage: 28199,
  pubsub: 28085,
  ui: 28000,
} as const;

// =============================================================================
// Firebase Initialization
// =============================================================================

/** Firebase app instance */
let app: FirebaseApp;

/** Firebase Auth instance */
let auth: Auth;

/** Firestore instance */
let db: Firestore;

/** Firebase Functions instance */
let functions: Functions;

/** Analytics instance (only in browser, not in emulator mode) */
let analytics: Analytics | null = null;

/**
 * Initialize Firebase app and services.
 * This is called once when the module loads.
 */
function initializeFirebase(): void {
  // Prevent multiple initializations - use getApp() which throws if no app exists
  if (getApps().length > 0) {
    app = getApp();
  } else {
    app = initializeApp(firebaseConfig);
  }

  // Initialize Auth
  auth = getAuth(app);

  // Initialize Firestore
  db = getFirestore(app);

  // Initialize Functions
  functions = getFunctions(app, 'us-central1');

  // Connect to emulators in development
  if (useEmulators) {
    console.log('🔧 Connecting to Firebase emulators...');
    
    try {
      connectAuthEmulator(auth, `http://localhost:${EMULATOR_PORTS.auth}`, {
        disableWarnings: true,
      });
      console.log(`  ✓ Auth emulator on port ${EMULATOR_PORTS.auth}`);
    } catch {
      // Already connected
    }

    try {
      connectFirestoreEmulator(db, 'localhost', EMULATOR_PORTS.firestore);
      console.log(`  ✓ Firestore emulator on port ${EMULATOR_PORTS.firestore}`);
    } catch {
      // Already connected
    }

    try {
      connectFunctionsEmulator(functions, 'localhost', EMULATOR_PORTS.functions);
      console.log(`  ✓ Functions emulator on port ${EMULATOR_PORTS.functions}`);
    } catch {
      // Already connected
    }
  }

  // Initialize Analytics (only in production, not with emulators)
  if (!useEmulators) {
    try {
      analytics = getAnalytics(app);
      console.log('📊 Analytics initialized');
    } catch (e) {
      console.warn('Analytics initialization failed:', e);
    }
  }
}

// Initialize on module load
initializeFirebase();

// =============================================================================
// Exports
// =============================================================================

export { app, auth, db, functions, analytics };

/**
 * Check if we're using emulators
 */
export const isUsingEmulators = useEmulators;

/**
 * Get the current Firebase project ID
 */
export const projectId = firebaseConfig.projectId;